import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { AdminGate, useAdminAccess } from '../components/AdminGate';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Megaphone, Loader2, Trash2, Edit, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../lib/api';

export default function AnnouncementsPage() {
  const adminAccess = useAdminAccess();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    is_active: true,
  });

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API_URL}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminAccess === 'yes') {
      loadAnnouncements();
    }
  }, [adminAccess]);

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (editingId) {
        await axios.put(`${API_URL}/admin/announcements/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API_URL}/admin/announcements`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Announcement created');
      }
      setDialogOpen(false);
      setFormData({ title: '', message: '', is_active: true });
      setEditingId(null);
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      is_active: announcement.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API_URL}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Announcement deleted');
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData({ title: '', message: '', is_active: true });
    setDialogOpen(true);
  };

  const access = adminAccess;
  if (access !== 'yes') return <AdminGate access={access} />;

  return (
    <Layout>
      <div className="container mx-auto space-y-8">
        <PageHeader title="Announcements" />
        <div className="flex items-center justify-end">
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4" /> New announcement
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All announcements</CardTitle>
            <CardDescription>{announcements.length} total announcements</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-12">
                <Megaphone className="w-12 h-12 text-ink-faded mx-auto mb-4" />
                <p className="text-muted-foreground">No announcements yet</p>
              </div>
            ) : (
              <div className="bg-surface rounded-cards overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {announcements.map((announcement) => (
                      <TableRow key={announcement.id}>
                        <TableCell className="font-medium">{announcement.title}</TableCell>
                        <TableCell className="max-w-md truncate">{announcement.message}</TableCell>
                        <TableCell>
                          <Badge variant={announcement.is_active ? 'default' : 'secondary'}>
                            {announcement.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(announcement.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(announcement)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(announcement.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit announcement' : 'New announcement'}</DialogTitle>
              <DialogDescription>
                Create a message to display to users on login. Supports formatted text and emojis.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Welcome to No Half Days MVP!"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Thank you for trying No Half Days! We're actively developing new features...\n\n✨ Coming Soon:\n• QuickBooks integration\n• Advanced reporting"
                  rows={10}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Supports line breaks and emojis. No length limit.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="active">Active (show to users)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingId ? 'Update' : 'Create'} announcement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
